<template>
  <ion-page :class="{ 'verification-modal--recording': phase === 'record' }">
    <ion-content
        :fullscreen="true"
        :scroll-y="false"
        :class="{ 'verification-content--transparent': phase === 'record' }">
      <div
          class="verification-sheet"
          :class="phase === 'record' ? 'verification-sheet--recording' : ''">
        <div
            class="verification-sheet__content"
            :class="{
              'verification-sheet__content--wide': phase === 'record',
              'verification-sheet__content--review': phase === 'review',
            }">

        <template v-if="phase === 'intro'">
          <div class="verification-sheet__intro">
            <div class="verification-sheet__icon">
              <svg class="h-5 fill-current text-brand-black" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
              </svg>
            </div>

            <div class="verification-sheet__copy">
              <h2 class="verification-sheet__title">Verification Required</h2>

              <template v-if="!coupleAccount">
                <p class="verification-sheet__text">
                  To ensure the safety of all members of the Pineapple community you must verify that you are who you say you are. Once submitted you will have view only access until your profile is verified by the Pineapple team.
                </p>
                <p class="verification-sheet__text verification-sheet__text--emphasis">
                  You'll record a short video selfie in the app and say the phrase below.
                </p>
              </template>

              <template v-else>
                <p class="verification-sheet__text">
                  To ensure the safety of all members of the Pineapple community
                  <span class="text-white">you must verify that you are a couple</span>.
                  Once submitted you will have view only access until your profile is verified by the Pineapple team.
                </p>
                <p class="verification-sheet__text verification-sheet__text--emphasis">
                  Record a short video selfie in the app with one of you saying the phrase below.
                </p>
                <div class="verification-sheet__notice">
                  You are verifying as a couple so you both MUST be present in the video or your verification will be changed to a solo account.
                </div>
              </template>
            </div>

            <div class="verification-sheet__phrase">
              <div class="verification-sheet__phrase-label">You'll be asked to say</div>
              <div class="verification-sheet__phrase-value">"{{ verificationPhrase }}"</div>
            </div>

            <div class="verification-sheet__actions verification-sheet__actions--single">
              <button
                  type="button"
                  @click="beginRecording"
                  class="verification-sheet__primary-btn">
                I'm ready to record
              </button>
            </div>

            <p class="verification-sheet__footnote">
              Verification videos are removed from our servers upon approval or rejection.
            </p>
          </div>
        </template>

        <template v-else-if="phase === 'record'">
          <VerificationRecorderView
              :couple-account="coupleAccount"
              :phrase="verificationPhrase"
              @cancel="phase = 'intro'"
              @recorded="handleRecorded"
              @error="showError"/>
        </template>

        <template v-else-if="phase === 'review'">
          <div class="verification-sheet__review">
            <div class="verification-sheet__copy">
              <h2 class="verification-sheet__title">Review your video</h2>
              <p class="verification-sheet__text">Make sure you're clearly visible and the phrase is easy to hear.</p>
            </div>

            <video
                v-if="reviewUrl"
                :src="reviewUrl"
                class="verification-sheet__video"
                controls
                playsinline/>

            <div v-if="uploadProgress > 0 && posting" class="space-y-2">
              <div class="h-1 w-full overflow-hidden rounded-full bg-brand-dark-gray">
                <div class="h-full bg-brand-lime transition-all" :style="{ width: `${uploadProgress}%` }"/>
              </div>
              <div class="text-center text-sm text-brand-light-gray">Uploading… {{ uploadProgress }}%</div>
            </div>

            <div class="verification-sheet__actions verification-sheet__actions--split">
              <button
                  type="button"
                  @click="retake"
                  :disabled="posting"
                  class="verification-sheet__secondary-btn">
                Retake
              </button>
              <button
                  type="button"
                  @click="submitVideo"
                  :disabled="posting || !recordedBlob"
                  class="verification-sheet__primary-btn verification-sheet__primary-btn--compact">
                <span v-if="!posting">Submit</span>
                <svg v-else class="h-5 w-5 animate-spin text-brand-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
              </button>
            </div>
          </div>
        </template>

        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {IonContent, IonPage, modalController, toastController} from '@ionic/vue';
import ApiService from '@/services/api/apiService';
import {Observable, ObservableEvent} from '@/services/observable/Observable';
import {AccountType} from '@/definitions/AccountType';
import {UserBasicInformationInterface} from '@/interfaces/UserBasicInformationInterface';
import VerificationRecorderView from '@/components/VerificationRecorderView.vue';
import {VERIFICATION_PHRASE} from '@/composables/useVerificationVideoRecorder';

type Phase = 'intro' | 'record' | 'review';

const phase = ref<Phase>('intro');
const authUser = ref<UserBasicInformationInterface>();
const recordedBlob = ref<Blob | null>(null);
const reviewUrl = ref<string | null>(null);
const posting = ref(false);
const uploadProgress = ref(0);

const verificationPhrase = VERIFICATION_PHRASE;

const coupleAccount = computed(() => {
  const type = authUser.value?.account_type;
  return type === AccountType.Couple || type === AccountType.Joint;
});

const cleanupReviewUrl = () => {
  if (reviewUrl.value) {
    URL.revokeObjectURL(reviewUrl.value);
    reviewUrl.value = null;
  }
};

const showError = async (message: string) => {
  const toast = await toastController.create({
    message,
    duration: 3500,
    position: 'bottom',
    color: 'danger',
  });
  await toast.present();
};

const beginRecording = () => {
  phase.value = 'record';
};

const handleRecorded = (blob: Blob) => {
  cleanupReviewUrl();
  recordedBlob.value = blob;
  reviewUrl.value = URL.createObjectURL(blob);
  phase.value = 'review';
};

const retake = () => {
  cleanupReviewUrl();
  recordedBlob.value = null;
  phase.value = 'record';
};

const submitVideo = async () => {
  if (!recordedBlob.value || posting.value) {
    return;
  }

  if (recordedBlob.value.size === 0) {
    await showError('Your recording is empty. Please record again.');
    return;
  }

  try {
    posting.value = true;
    uploadProgress.value = 0;

    const mimeType = (recordedBlob.value.type || 'video/webm').split(';')[0];
    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedBlob.value], `verification.${extension}`, {
      type: mimeType,
    });

    await ApiService.User().attachVerificationVideo(file, (progress: number) => {
      uploadProgress.value = parseInt(`${progress}`, 10);
    });

    Observable.emit(ObservableEvent.UserChanged);

    const toast = await toastController.create({
      message: 'Thank you for submitting your verification video. Our team aim to review all verification submissions within 48 hours of submission.',
      duration: 3500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();

    await modalController.dismiss();
  } catch (e: any) {
    const message = e?.data?.message
        || e?.message
        || (Array.isArray(e?.errors?.video) ? e.errors.video[0] : null)
        || 'Unable to upload your verification video. Please try again.';
    await showError(message);
  } finally {
    posting.value = false;
  }
};

onBeforeUnmount(cleanupReviewUrl);

onMounted(async () => {
  authUser.value = await ApiService.User().authUser();
});
</script>

<style scoped>
.verification-sheet {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-height: 100%;
  justify-content: center;
  background: #1A1B22;
  padding:
      max(1rem, env(safe-area-inset-top, 0px))
      max(1rem, env(safe-area-inset-right, 0px))
      max(1rem, env(safe-area-inset-bottom, 0px))
      max(1rem, env(safe-area-inset-left, 0px));
}

.verification-sheet--recording {
  background: transparent;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  padding:
      max(0.75rem, env(safe-area-inset-top, 0px))
      max(0.75rem, env(safe-area-inset-right, 0px))
      max(0.75rem, env(safe-area-inset-bottom, 0px))
      max(0.75rem, env(safe-area-inset-left, 0px));
}

.verification-sheet__content {
  box-sizing: border-box;
  width: 100%;
  max-width: 26rem;
  margin: 0 auto;
}

.verification-sheet__content--wide,
.verification-sheet__content--review {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 26rem;
  height: 100%;
  min-height: 0;
}

.verification-sheet__intro {
  display: flex;
  width: 100%;
  max-height: 100%;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.875rem, 2.5vw, 1.5rem);
}

.verification-sheet__review {
  display: flex;
  width: 100%;
  max-height: 100%;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.875rem, 2.5vw, 1.5rem);
}

.verification-sheet__icon {
  display: flex;
  height: 2.5rem;
  width: 2.5rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #A5A7AF;
}

.verification-sheet__copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.625rem;
  text-align: center;
}

.verification-sheet__title {
  margin: 0;
  color: #fff;
  font-size: clamp(1.2rem, 3.5vw, 1.5rem);
  font-weight: 500;
  line-height: 1.2;
}

.verification-sheet__text {
  margin: 0;
  color: #A5A7AF;
  font-size: clamp(0.875rem, 2.8vw, 0.975rem);
  line-height: 1.45;
}

.verification-sheet__text--emphasis {
  color: #fff;
}

.verification-sheet__notice {
  border-radius: 0.75rem;
  border: 1px solid #EBFF00;
  background: rgb(235 255 0 / 0.1);
  padding: 0.75rem;
  color: #EBFF00;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.45;
}

.verification-sheet__actions--single,
.verification-sheet__footnote {
  flex-shrink: 0;
}

.verification-sheet__phrase {
  flex-shrink: 0;
  width: 100%;
  border-radius: 1rem;
  border: 1px solid rgb(235 255 0 / 0.3);
  background: #262A34;
  padding: 0.875rem;
}

.verification-sheet__phrase-label {
  color: #A5A7AF;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.verification-sheet__phrase-value {
  margin-top: 0.375rem;
  color: #EBFF00;
  font-size: clamp(0.95rem, 3.2vw, 1.1rem);
  font-weight: 600;
  line-height: 1.35;
}

.verification-sheet__actions {
  display: flex;
  width: 100%;
  justify-content: center;
}

.verification-sheet__actions--single {
  padding-top: 0.25rem;
}

.verification-sheet__actions--split {
  gap: 0.75rem;
}

.verification-sheet__primary-btn,
.verification-sheet__secondary-btn {
  box-sizing: border-box;
  display: inline-flex;
  min-height: 3.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  font-weight: 500;
  line-height: 1.2;
}

.verification-sheet__primary-btn {
  width: min(100%, 16rem);
  background: #EBFF00;
  padding: 0.75rem 2rem;
  color: #1A1B22;
  font-size: clamp(1rem, 3.6vw, 1.15rem);
}

.verification-sheet__primary-btn--compact {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  padding-inline: 1.5rem;
  font-size: 1rem;
}

.verification-sheet__secondary-btn {
  flex: 1 1 0;
  min-width: 0;
  background: #262A34;
  padding: 0.75rem 1.5rem;
  color: #fff;
  font-size: 1rem;
}

.verification-sheet__primary-btn:disabled,
.verification-sheet__secondary-btn:disabled {
  opacity: 0.4;
}

.verification-sheet__footnote {
  margin: 0;
  max-width: 22rem;
  padding-inline: 0.5rem;
  color: #A5A7AF;
  font-size: 0.875rem;
  line-height: 1.45;
  text-align: center;
}

.verification-sheet__video {
  width: 100%;
  max-height: min(16rem, 38vh);
  aspect-ratio: 3 / 4;
  border-radius: 1rem;
  background: #262A34;
  object-fit: cover;
}

@media (min-width: 768px) {
  .verification-sheet {
    align-items: stretch;
    padding: 1.25rem;
  }

  .verification-sheet--recording {
    background: #1A1B22;
    align-items: center;
    justify-content: center;
  }

  .verification-sheet__content,
  .verification-sheet__content--wide,
  .verification-sheet__content--review {
    max-width: 22rem;
  }

  .verification-sheet__intro {
    gap: 1rem;
  }

  .verification-sheet__primary-btn {
    width: auto;
    min-width: 14rem;
  }

  .verification-sheet__actions--split {
    max-width: 100%;
    margin-inline: auto;
  }

  .verification-sheet__video {
    max-height: min(14rem, 34vh);
  }
}

@media (max-height: 760px) {
  .verification-sheet__intro,
  .verification-sheet__review {
    gap: 0.75rem;
  }

  .verification-sheet__footnote {
    font-size: 0.8125rem;
  }
}

:global(.verification-modal--recording),
:global(.verification-modal--recording ion-content),
:global(.verification-content--transparent) {
  --background: transparent;
  background: transparent !important;
}

@media (min-width: 768px) {
  :global(ion-modal.verification-fullscreen-modal ion-content) {
    --overflow: hidden;
    height: 100%;
  }

  :global(.verification-modal--recording),
  :global(.verification-modal--recording ion-content),
  :global(.verification-content--transparent) {
    --background: #1A1B22;
    background: #1A1B22 !important;
  }

  .verification-sheet--recording {
    background: #1A1B22;
  }
}
</style>
